import React, { useMemo } from 'react'
import {
  AbsoluteFill,
  Easing,
  interpolate,
  random,
  useCurrentFrame,
  useVideoConfig,
} from 'remotion'
import {
  ResponsiveSceneProvider,
  useResponsiveSceneProps,
} from '../components/ResponsiveSceneContext'
import { SceneProps } from '../types'

const TITLE_TEXT = `Reactions`
const DEG_TO_RAD = Math.PI / 180

export function ReactionsScene({ audiogram, orientation, scene }: SceneProps) {
  return (
    <ResponsiveSceneProvider
      audiogram={audiogram}
      scene={scene}
      orientation={orientation}
    >
      <Background />

      <Circles debug={false} reactions={audiogram.reactions ?? []} />

      <StarLine />
      <TitleText text={TITLE_TEXT} />
    </ResponsiveSceneProvider>
  )
}

const Background = () => {
  const { audiogram } = useResponsiveSceneProps()

  return (
    <AbsoluteFill style={{ backgroundColor: audiogram.theme.colors.primary }} />
  )
}

const StarSVG = ({
  className,
  style,
}: {
  className?: string
  style?: React.CSSProperties
}) => {
  return (
    <svg
      className={className}
      style={style}
      width="44"
      height="44"
      viewBox="0 0 44 44"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M-1.909e-06 22.2014C19.5136 20.9333 20.5304 19.9164 21.7986 0.402826C23.0667 19.9164 24.0835 20.9333 43.5972 22.2014C24.0835 23.4696 23.0667 24.4864 21.7986 44C20.5304 24.4864 19.5136 23.4696 -1.909e-06 22.2014Z"
        fill="currentColor"
      />
    </svg>
  )
}

const StarLine = () => {
  const { audiogram, u, w, h } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const rate = 0.25 * 360 * time

  const enterDriver = interpolate(time, [0, 1], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
    easing: Easing.out(Easing.exp),
  })

  const x = enterDriver * (w() - u(2))

  return (
    <>
      <div
        className="absolute -translate-y-1/2"
        style={{
          backgroundColor: audiogram.theme.colors.secondary,
          top: u(6),
          width: x,
          height: u(0.125),
        }}
      />
      <StarSVG
        className="absolute"
        style={{
          color: audiogram.theme.colors.secondary,
          top: u(6),
          width: u(3),
          height: u(3),
          transform: `translate(-50%,-50%) translateX(${x}px) rotate(${-rate}deg)`,
        }}
      />
    </>
  )
}

const TitleText = ({ text }: { text: string }) => {
  const { audiogram, u, w, h, orientation } = useResponsiveSceneProps()

  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()

  const time = frame / fps

  const enterDriver = interpolate(time, [0, 0.5], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  })

  const opacity = enterDriver
  const x = interpolate(enterDriver, [0, 1], [-u(2), 0], {
    easing: Easing.out(Easing.exp),
  })

  return (
    <div
      className="absolute flex flex-row items-center overflow-hidden text-6xl"
      style={{
        color: audiogram.theme.colors.secondary,
        right: u(2),
        height: u(5),
        left: u(1.5),
        top: u(0.5),
        fontFamily: audiogram.theme.fonts.primary,
        lineHeight: 1.2,
        opacity,
        transform: `translateX(${x}px)`,
      }}
    >
      Reactions
    </div>
  )
}

const EPSILON = 1e-6

function isCircleInsideCircle(
  circle: { x: number; y: number; radius: number },
  otherCircle: { x: number; y: number; radius: number },
) {
  const { radius: r1 } = circle
  const { radius: r2 } = otherCircle

  return distance(circle, otherCircle) - (r1 + r2) < -EPSILON
}

function isValidPlacement(
  circle: { x: number; y: number; radius: number },
  otherCircles: { x: number; y: number; radius: number }[],
) {
  return otherCircles.every(
    (otherCircle) => !isCircleInsideCircle(circle, otherCircle),
  )
}

type Circle = {
  label: string
  value: number
  radius: number
}

type PlacedCircle = Circle & {
  x: number
  y: number
}

function placeCircles({
  circles,
  bounds,
}: {
  circles: Circle[]
  bounds: {
    width: number
    height: number
  }
}) {
  const placedCircles: PlacedCircle[] = []

  for (let i = 0; i < circles.length; i++) {
    const circle = circles[i]!

    switch (i) {
      case 0: {
        placedCircles.push({
          x: 0.5 * bounds.width,
          y: 0.5 * bounds.height,
          ...circle,
        })
        break
      }

      case 1: {
        const lastCircle = placedCircles[placedCircles.length - 1]!

        const d = circle.radius + lastCircle.radius
        const a = random(circle.radius) * 2 * Math.PI

        placedCircles.push({
          x: lastCircle.x + d * Math.cos(a),
          y: lastCircle.y + d * Math.sin(a),
          ...circle,
        })
        break
      }

      default: {
        const [a, b] = placedCircles.slice(-2)

        const xDiff = b!.x - a!.x
        const yDiff = b!.y - a!.y
        const angle_base = Math.atan(yDiff / xDiff)

        // the centerpoints of the three circles forms a triangle
        // get the length of all the sides of this triangle
        const length_ab = a!.radius + b!.radius
        const length_bc = b!.radius + circle.radius
        const length_ac = a!.radius + circle.radius

        const angle_b = Math.acos(
          (length_ac ** 2 + length_ab ** 2 - length_bc ** 2) /
            (2 * length_ab * length_ac),
        )

        let d = length_ac
        let angle = angle_base + angle_b + (xDiff < 0 ? Math.PI : 0)

        let x = a!.x
        let y = a!.y

        const newCircle = {
          x: x + d * Math.cos(angle),
          y: y + d * Math.sin(angle),
          ...circle,
        }

        let attempts = 0

        // try placing around circle a
        while (!isValidPlacement(newCircle, placedCircles)) {
          angle += attempts * DEG_TO_RAD

          newCircle.x = x + d * Math.cos(angle)
          newCircle.y = y + d * Math.sin(angle)

          // try placing
          if (attempts++ > 360) {
            break
          }
        }

        // ok we couldn't place around circle a, try around circle b
        attempts = 0
        d = length_bc
        x = b!.x
        y = b!.y

        // try placing around circle b
        while (!isValidPlacement(newCircle, placedCircles)) {
          angle += attempts * DEG_TO_RAD

          newCircle.x = x + d * Math.cos(angle)
          newCircle.y = y + d * Math.sin(angle)

          // try placing
          if (attempts++ > 360) {
            break
          }
        }

        placedCircles.push(newCircle)
        break
      }
    }
  }

  return placedCircles
}

interface Point {
  x: number
  y: number
}

// create a convex hull from a set of points
function convexHullFromPoints(points: Point[]): Point[] {
  // If there are less than 3 points, they are already a convex hull
  if (points.length < 3) {
    return points
  }

  // Find the point with the lowest x-coordinate (ties broken by lowest y-coordinate)
  let startPoint: Point = points[0]!
  for (const point of points) {
    if (
      point.x < startPoint.x ||
      (point.x === startPoint.x && point.y < startPoint.y)
    ) {
      startPoint = point
    }
  }

  // Begin wrapping from the starting point
  const hull: Point[] = [startPoint]
  let currentPoint = startPoint

  do {
    // Find the next point on the hull
    let nextPoint = points[0]!
    for (const point of points) {
      if (point === currentPoint) {
        continue
      }

      const crossProduct =
        (nextPoint.x - currentPoint.x) * (point.y - currentPoint.y) -
        (nextPoint.y - currentPoint.y) * (point.x - currentPoint.x)

      if (
        nextPoint === currentPoint ||
        crossProduct > 0 ||
        (crossProduct === 0 &&
          distance(currentPoint, point) > distance(currentPoint, nextPoint))
      ) {
        nextPoint = point
      }
    }

    // Add the next point to the hull and update the current point
    hull.push(nextPoint)
    currentPoint = nextPoint
  } while (currentPoint !== startPoint)

  return hull
}

// Create tight convex hull around the circles
function convexHullFromCircles(circles: PlacedCircle[]): Point[] {
  // Create a list of all points on the circles
  const allPoints: Point[] = []
  for (const circle of circles) {
    for (let angle = 0; angle < 2 * Math.PI; angle += Math.PI / 12) {
      const x = circle.x + circle.radius * Math.cos(angle)
      const y = circle.y + circle.radius * Math.sin(angle)
      allPoints.push({ x, y })
    }
  }

  // Sort the points by x coordinate and then by y coordinate
  allPoints.sort((a, b) => {
    if (a.x < b.x) {
      return -1
    } else if (a.x > b.x) {
      return 1
    } else if (a.y < b.y) {
      return -1
    } else if (a.y > b.y) {
      return 1
    } else {
      return 0
    }
  })

  // Create a list of upper hull points
  const upperHull: Point[] = []
  for (const point of allPoints) {
    while (upperHull.length >= 2) {
      const last = upperHull[upperHull.length - 1]!
      const secondLast = upperHull[upperHull.length - 2]!
      const vector1 = { x: point.x - secondLast.x, y: point.y - secondLast.y }
      const vector2 = { x: last.x - secondLast.x, y: last.y - secondLast.y }
      if (vector1.x * vector2.y - vector1.y * vector2.x >= 0) {
        break
      }
      upperHull.pop()
    }
    upperHull.push(point)
  }

  // Create a list of lower hull points
  const lowerHull: Point[] = []
  for (let i = allPoints.length - 1; i >= 0; i--) {
    const point = allPoints[i]!
    while (lowerHull.length >= 2) {
      const last = lowerHull[lowerHull.length - 1]!
      const secondLast = lowerHull[lowerHull.length - 2]!
      const vector1 = { x: point.x - secondLast.x, y: point.y - secondLast.y }
      const vector2 = { x: last.x - secondLast.x, y: last.y - secondLast.y }
      if (vector1.x * vector2.y - vector1.y * vector2.x >= 0) {
        break
      }
      lowerHull.pop()
    }
    lowerHull.push(point)
  }

  // Combine the upper and lower hulls
  upperHull.pop()
  lowerHull.pop()
  const convexHull: Point[] = upperHull.concat(lowerHull)

  // Return the convex hull
  return convexHull
}

function distance(a: Point, b: Point): number {
  return Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2)
}

function magnitude(a: Point): number {
  return Math.sqrt(a.x ** 2 + a.y ** 2)
}

function boundingBox(convexHull: Point[]): Point[] {
  // Find the angle of each line segment in the convex hull
  const angles: number[] = []
  for (let i = 0; i < convexHull.length; i++) {
    const j = (i + 1) % convexHull.length
    angles.push(
      Math.atan2(
        convexHull[j]!.y - convexHull[i]!.y,
        convexHull[j]!.x - convexHull[i]!.x,
      ),
    )
  }

  // Find the minimum and maximum angles
  let minAngle = angles[0]!
  let maxAngle = angles[0]!
  for (const angle of angles) {
    if (angle < minAngle) {
      minAngle = angle
    }
    if (angle > maxAngle) {
      maxAngle = angle
    }
  }

  // Rotate the convex hull by the negative of the minimum angle
  const rotatedHull: Point[] = []
  for (const point of convexHull) {
    const x = point.x * Math.cos(-minAngle) - point.y * Math.sin(-minAngle)
    const y = point.x * Math.sin(-minAngle) + point.y * Math.cos(-minAngle)
    rotatedHull.push({ x, y })
  }

  // Find the minimum and maximum x and y values in the rotated hull
  let minX = rotatedHull[0]!.x
  let maxX = rotatedHull[0]!.x
  let minY = rotatedHull[0]!.y
  let maxY = rotatedHull[0]!.y
  for (const point of rotatedHull) {
    if (point.x < minX) {
      minX = point.x
    }
    if (point.x > maxX) {
      maxX = point.x
    }
    if (point.y < minY) {
      minY = point.y
    }
    if (point.y > maxY) {
      maxY = point.y
    }
  }

  // Rotate the bounding box back to its original orientation
  const box: Point[] = [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
  ]
  for (const point of box) {
    const x = point.x * Math.cos(minAngle) - point.y * Math.sin(minAngle)
    const y = point.x * Math.sin(minAngle) + point.y * Math.cos(minAngle)
    point.x = x
    point.y = y
  }

  return box
}

function getAngleBetweenVectors(vec1: Point, vec2: Point): number {
  // Calculate the dot product of the vectors
  const dotProduct = vec1.x * vec2.x + vec1.y * vec2.y

  // Calculate the magnitude of the vectors
  const magnitude1 = magnitude(vec1)
  const magnitude2 = magnitude(vec2)

  // Calculate the cosine of the angle between the vectors
  const cosine = dotProduct / (magnitude1 * magnitude2)

  // Calculate the angle in radians
  const angle = Math.acos(cosine)

  // Return the angle in radians
  return angle
}

function rotatePoint<T extends Point>(point: T, angle: number): T {
  // Rotate the point using the rotation matrix
  const x = point.x * Math.cos(angle) - point.y * Math.sin(angle)
  const y = point.x * Math.sin(angle) + point.y * Math.cos(angle)

  // Return the rotated point
  return { ...point, x, y }
}

function translatePoint<T extends Point>(point: T, distance: Point): T {
  // Translate the point by the distance
  const x = point.x + distance.x
  const y = point.y + distance.y

  // Return the translated point
  return { ...point, x, y }
}

type TransformationMatrix = {
  a: number
  b: number
  c: number
  d: number
  e: number
  f: number
}

function transformPoint<T extends Point>(
  point: T,
  transform: TransformationMatrix,
): T {
  // Transform the point using the transformation matrix
  const x = transform.a * point.x + transform.c * point.y + transform.e
  const y = transform.b * point.x + transform.d * point.y + transform.f

  // Return the transformed point
  return { ...point, x, y }
}

function getTransformationMatrixForRectangleInBoundingBox(
  rectangle: Point[],
  boundingBox: Point[],
): TransformationMatrix {
  // Find the minimum and maximum x and y coordinates of the rectangle and bounding box
  let rectXMin = rectangle[0]!.x
  let rectXMax = rectangle[0]!.x
  let rectYMin = rectangle[0]!.y
  let rectYMax = rectangle[0]!.y
  for (const point of rectangle) {
    rectXMin = Math.min(rectXMin, point.x)
    rectXMax = Math.max(rectXMax, point.x)
    rectYMin = Math.min(rectYMin, point.y)
    rectYMax = Math.max(rectYMax, point.y)
  }

  let boxXMin = boundingBox[0]!.x
  let boxXMax = boundingBox[0]!.x
  let boxYMin = boundingBox[0]!.y
  let boxYMax = boundingBox[0]!.y
  for (const point of boundingBox) {
    boxXMin = Math.min(boxXMin, point.x)
    boxXMax = Math.max(boxXMax, point.x)
    boxYMin = Math.min(boxYMin, point.y)
    boxYMax = Math.max(boxYMax, point.y)
  }

  // Calculate the width and height of the rectangle and bounding box
  const rectWidth = rectXMax - rectXMin
  const rectHeight = rectYMax - rectYMin
  const boxWidth = boxXMax - boxXMin
  const boxHeight = boxYMax - boxYMin

  // Calculate the scaling factor for the rectangle to fit inside the bounding box
  const scaleX = boxWidth / rectWidth
  const scaleY = boxHeight / rectHeight
  const scale = Math.min(scaleX, scaleY)

  // Calculate the translation to center the rectangle inside the bounding box
  const rectCenterX = rectXMin + rectWidth / 2
  const rectCenterY = rectYMin + rectHeight / 2
  const boxCenterX = boxXMin + boxWidth / 2
  const boxCenterY = boxYMin + boxHeight / 2
  const translateX = boxCenterX - scale * rectCenterX
  const translateY = boxCenterY - scale * rectCenterY

  // Create the transformation matrix
  const transformationMatrix: TransformationMatrix = {
    a: scale,
    b: 0,
    c: 0,
    d: scale,
    e: translateX,
    f: translateY,
  }

  // Return the transformation matrix
  return transformationMatrix
}

type BoundingBox = {
  xMin: number
  yMin: number
  xMax: number
  yMax: number
}

function getBoundingBoxForCircles(circles: PlacedCircle[]): BoundingBox {
  // Find the minimum and maximum x and y coordinates of the circles
  let xMin = circles[0]!.x - circles[0]!.radius
  let xMax = circles[0]!.x + circles[0]!.radius
  let yMin = circles[0]!.y - circles[0]!.radius
  let yMax = circles[0]!.y + circles[0]!.radius

  for (const circle of circles) {
    xMin = Math.min(xMin, circle.x - circle.radius)
    xMax = Math.max(xMax, circle.x + circle.radius)
    yMin = Math.min(yMin, circle.y - circle.radius)
    yMax = Math.max(yMax, circle.y + circle.radius)
  }

  // Return the bounding box
  return { xMin, yMin, xMax, yMax }
}

type Reaction = {
  emoji: string
  count: number
}

const Circles = ({
  reactions,
  debug = false,
}: {
  reactions: Reaction[]
  debug?: boolean
}) => {
  const { audiogram, w, h, u, orientation } = useResponsiveSceneProps()
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  const time = frame / fps

  const bounds = useMemo(
    () => ({ x: u(), y: u(7), width: w() - u(2), height: h() - u(8) }),
    [h, u, w],
  )

  const circles = useMemo(() => {
    const minCount = Math.min(...reactions.map((reaction) => reaction.count))
    const maxCount = Math.max(...reactions.map((reaction) => reaction.count))

    const circles = reactions.map((reaction) => ({
      value: reaction.count,
      label: reaction.emoji,
      radius: interpolate(reaction.count, [minCount, maxCount], [30, 100]),
    }))

    return circles
  }, [reactions])

  const circlesLayout = useMemo(() => {
    const placedCircles = placeCircles({ circles, bounds })

    // const hull = convexHullFromCircles(placedCircles)
    const hull = convexHullFromPoints(placedCircles)
    const arbitraryAngleBoundingBox = boundingBox(hull)

    const vecA: Point = {
      x: arbitraryAngleBoundingBox[0]!.x - arbitraryAngleBoundingBox[1]!.x,
      y: arbitraryAngleBoundingBox[0]!.y - arbitraryAngleBoundingBox[1]!.y,
    }
    const vecB: Point = {
      x: arbitraryAngleBoundingBox[0]!.x - arbitraryAngleBoundingBox[3]!.x,
      y: arbitraryAngleBoundingBox[0]!.y - arbitraryAngleBoundingBox[3]!.y,
    }

    const magnitudeA = magnitude(vecA)
    const magnitudeB = magnitude(vecB)

    const biggestVector = magnitudeA > magnitudeB ? vecA : vecB

    const angle = getAngleBetweenVectors(biggestVector, { x: 1, y: 0 })

    function axisAlign<T extends Point>(p: T): T {
      return translatePoint(
        rotatePoint(
          translatePoint(p, {
            x: -bounds.width / 2,
            y: -bounds.height / 2,
          }),
          bounds.width > bounds.height ? -angle : -angle - Math.PI / 2,
        ),
        {
          x: bounds.width / 2,
          y: bounds.height / 2,
        },
      )
    }

    // rotate placedCircles around stage center by angle
    const rotatedCircles = placedCircles.map(axisAlign)
    // rotate hull by angle
    const rotatedHull = hull.map(axisAlign)
    // rotate bounding box by angle
    const rotatedBoundingBox = arbitraryAngleBoundingBox.map(axisAlign)

    const alignedBoundingBox = getBoundingBoxForCircles(rotatedCircles)

    const fitTransformMatrix = getTransformationMatrixForRectangleInBoundingBox(
      [
        { x: alignedBoundingBox.xMin, y: alignedBoundingBox.yMin },
        { x: alignedBoundingBox.xMax, y: alignedBoundingBox.yMin },
        { x: alignedBoundingBox.xMax, y: alignedBoundingBox.yMax },
        { x: alignedBoundingBox.xMin, y: alignedBoundingBox.yMax },
      ],
      [
        { x: bounds.x, y: bounds.y },
        { x: bounds.x + bounds.width, y: bounds.y },
        { x: bounds.x + bounds.width, y: bounds.y + bounds.height },
        { x: bounds.x, y: bounds.y + bounds.height },
      ],
    )

    const transformedCircles = rotatedCircles.map((circle) => {
      const transformedCircle = transformPoint(circle, fitTransformMatrix)
      return {
        ...transformedCircle,
        radius: circle.radius * fitTransformMatrix.a,
      }
    })
    const transformedHull = rotatedHull.map((point) =>
      transformPoint(point, fitTransformMatrix),
    )
    const transformedBoundingBox = rotatedBoundingBox.map((point) =>
      transformPoint(point, fitTransformMatrix),
    )
    const transformedAlignedBoundingBox = [
      transformPoint(
        { x: alignedBoundingBox.xMin, y: alignedBoundingBox.yMin },
        fitTransformMatrix,
      ),
      transformPoint(
        { x: alignedBoundingBox.xMax, y: alignedBoundingBox.yMin },
        fitTransformMatrix,
      ),
      transformPoint(
        { x: alignedBoundingBox.xMax, y: alignedBoundingBox.yMax },
        fitTransformMatrix,
      ),
      transformPoint(
        { x: alignedBoundingBox.xMin, y: alignedBoundingBox.yMax },
        fitTransformMatrix,
      ),
    ]

    return {
      circles: transformedCircles,
      hull: transformedHull,
      hullBoundingBox: transformedBoundingBox,
      boundingBox: transformedAlignedBoundingBox,
    }
  }, [circles, bounds])

  const item = (t: number) => {
    const opacity = interpolate(t, [0, 0.2], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
    })
    const scale = interpolate(t, [0, 0.5], [0, 1], {
      extrapolateLeft: 'clamp',
      extrapolateRight: 'clamp',
      easing: Easing.out(Easing.exp),
    })

    return {
      opacity,
      transform: `translate(-50%, -50%) scale(${scale})`,
    }
  }

  const labelCircleSizing = {
    landscape: {
      width: u(3),
      height: u(3),
      borderRadius: u(3),
      fontSize: u(1),
    },
    portrait: {
      width: u(3),
      height: u(3),
      borderRadius: u(3),
      fontSize: u(1),
    },
    square: {
      width: u(2),
      height: u(2),
      borderRadius: u(2),
      fontSize: u(0.75),
    },
  }[orientation]

  return (
    <div
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        width: 0,
        bottom: 0,
        color: audiogram.theme.colors.secondary,
      }}
    >
      {circlesLayout.circles.map((c, i) => (
        <div
          key={i}
          className="flex items-center justify-center"
          style={{
            position: 'absolute',
            left: c.x,
            top: c.y,
            width: 2 * c.radius,
            height: 2 * c.radius,
            border: `solid 1px ${audiogram.theme.colors.secondary}`,
            borderRadius: c.radius,
            fontSize: 1 * c.radius,
            ...item(time - 0.05 * (i + 1)),
          }}
        >
          {c.label}
        </div>
      ))}
      {circlesLayout.circles.map((c, i) => (
        <div
          key={i}
          className="flex items-center justify-center"
          style={{
            position: 'absolute',
            left: c.x + c.radius * Math.cos((5 * Math.PI) / 4),
            top: c.y + c.radius * Math.sin((5 * Math.PI) / 4),
            border: `solid 1px ${audiogram.theme.colors.secondary}`,
            color: audiogram.theme.colors.secondary,
            backgroundColor: audiogram.theme.colors.primary,
            ...labelCircleSizing,
            ...item(time - 0.05 * (i + 1)),
          }}
        >
          {c.value}
        </div>
      ))}
      {debug && (
        <>
          <svg
            style={{
              position: 'absolute',
              left: bounds.x,
              top: bounds.y,
              width: bounds.width,
              height: bounds.height,
            }}
            viewBox={`${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`}
          >
            <path
              d={
                `M ${circlesLayout.hull[0]!.x},${circlesLayout.hull[0]!.y} ` +
                circlesLayout.hull
                  .slice(1)
                  .map((p) => `${p.x},${p.y}`)
                  .join(' L ')
                  .concat(' Z')
              }
              fill="none"
              stroke={audiogram.theme.colors.secondary}
              strokeDasharray={5}
              strokeWidth={1}
            />
            <path
              d={
                `M ${circlesLayout.hullBoundingBox[0]!.x},${circlesLayout.hullBoundingBox[0]!.y} ` +
                circlesLayout.hullBoundingBox

                  .slice(1)
                  .map((p) => `${p.x},${p.y}`)
                  .join(' L ')
                  .concat(' Z')
              }
              fill="none"
              stroke="red"
              strokeDasharray={5}
              strokeWidth={1}
            />
            <path
              d={
                `M ${circlesLayout.boundingBox[0]!.x},${circlesLayout.boundingBox[0]!.y} ` +
                circlesLayout.boundingBox

                  .slice(1)
                  .map((p) => `${p.x},${p.y}`)
                  .join(' L ')
                  .concat(' Z')
              }
              fill="none"
              stroke="magenta"
              strokeDasharray={5}
              strokeWidth={1}
            />
          </svg>
          <AbsoluteFill
            style={{
              border: `dashed 1px ${audiogram.theme.colors.secondary}`,
              left: bounds.x,
              top: bounds.y,
              width: bounds.width,
              height: bounds.height,
            }}
          />
        </>
      )}
    </div>
  )
}
